import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { auth, SESSION_EXPIRED_EVENT, subscriptions } from '../api/client';
import { ApiStatusChips } from './ApiStatusChips';
import { NotificationBell } from './NotificationBell';
import { SignedOutCard } from './SignedOutCard';
import { ToastProvider } from './Toast';

interface NavItem {
  to: string;
  label: string;
  icon: ReactElement;
}

const ICON_STROKE = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

function IconSubscriptions() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2.5" {...ICON_STROKE} />
      <path d="M3 10h18" {...ICON_STROKE} />
      <path d="M7 15h4" {...ICON_STROKE} />
    </svg>
  );
}

function IconPayments() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="2.5" {...ICON_STROKE} />
      <path d="M3 9h18M8 13h3M8 16.5h6" {...ICON_STROKE} />
    </svg>
  );
}

function IconReports() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M5 20V9m7 11V4m7 16v-7" {...ICON_STROKE} />
    </svg>
  );
}

function IconSettings() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle cx="12" cy="12" r="3" {...ICON_STROKE} />
      <path d="M12 3v3m0 12v3M4.2 7.5l2.6 1.5m10.4 6 2.6 1.5M4.2 16.5l2.6-1.5m10.4-6 2.6-1.5" {...ICON_STROKE} />
    </svg>
  );
}

const NAV_ITEMS: NavItem[] = [
  { to: '/subscriptions', label: 'Subscriptions', icon: <IconSubscriptions /> },
  { to: '/payments', label: 'Payments', icon: <IconPayments /> },
  { to: '/reports', label: 'Reports', icon: <IconReports /> },
  { to: '/settings', label: 'Settings', icon: <IconSettings /> },
];

const THEME_KEY = 'subs-theme';

/** `localStorage`-backed light/dark; defaults to light (the iSmart look). */
function useTheme(): [boolean, () => void] {
  const [dark, setDark] = useState(() => window.localStorage.getItem(THEME_KEY) === 'dark');

  useEffect(() => {
    document.documentElement.dataset.theme = dark ? 'dark' : 'light';
    window.localStorage.setItem(THEME_KEY, dark ? 'dark' : 'light');
  }, [dark]);

  return [dark, () => setDark((value) => !value)];
}

function GlobalSearch() {
  const [term, setTerm] = useState('');
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ['subscriptions', 'search', term],
    queryFn: ({ signal }) => subscriptions.list({ q: term, limit: 6 }, signal),
    enabled: term.trim().length >= 2,
    staleTime: 10_000,
    placeholderData: (previous) => previous,
  });

  const results = data?.items ?? [];
  const open = term.trim().length >= 2;

  return (
    <div className="search" role="search">
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className="search__icon">
        <circle cx="11" cy="11" r="6.5" {...ICON_STROKE} />
        <path d="m16 16 4 4" {...ICON_STROKE} />
      </svg>
      <input
        className="input search__input"
        type="search"
        placeholder="Search subscriptions…"
        aria-label="Search subscriptions"
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setTerm('');
          if (event.key === 'Enter' && results[0]) {
            navigate(`/subscriptions?q=${encodeURIComponent(term)}`);
            setTerm('');
          }
        }}
      />
      {open ? (
        results.length > 0 ? (
          <ul className="search__results">
            {results.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => {
                    navigate(`/subscriptions?q=${encodeURIComponent(item.name)}`);
                    setTerm('');
                  }}
                >
                  <span className="search__name">{item.name}</span>
                  <span className="search__meta">{item.group?.name ?? 'No group'}</span>
                </button>
              </li>
            ))}
            <li>
              <button
                type="button"
                onClick={() => {
                  navigate(`/subscriptions?q=${encodeURIComponent(term)}`);
                  setTerm('');
                }}
              >
                <span className="search__name">Search the list for “{term}”</span>
                <span className="search__meta">↵</span>
              </button>
            </li>
          </ul>
        ) : (
          <div className="search__results search__results--empty">
            Nothing matches “{term}”.{' '}
            <button type="button" className="link-btn" onClick={() => navigate(`/subscriptions?q=${encodeURIComponent(term)}`)}>
              Search the list anyway
            </button>
          </div>
        )
      ) : null}
    </div>
  );
}

/**
 * Session chip (Phase 9; role added in TASKS.md B7). Three outcomes:
 *  - `authDisabled`         -> nothing rendered: local dev has no login to show.
 *  - `authenticated: false` -> nothing rendered either (the layout itself swaps
 *    to the signed-out card). `/auth/me` answers that as data (HTTP 200), and
 *    the chip deliberately does NOT start a login from it: bouncing to
 *    /auth/login from a background probe is the silent re-login the logout
 *    flow was built to avoid. A 401 from any *data* endpoint is what flips the
 *    layout (the api client signals it, the shell listens).
 *  - authenticated          -> the user chip + logout.
 */
function UserChip() {
  const me = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: ({ signal }) => auth.me(signal),
    staleTime: 60_000,
    retry: false,
  });

  const user = me.data?.user;
  if (!me.data?.authenticated || me.data?.authDisabled || !user) return null;

  const initials = user.name
    .split(/\s+/)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('');

  return (
    <div className="user" title={user.email}>
      <span className="user__avatar" aria-hidden="true">{initials || user.email[0]?.toUpperCase()}</span>
      <span className="user__meta">
        <span className="user__name">{user.name}</span>
        <span className="user__email">{user.email}</span>
        <span className="user__role" style={{ fontSize: 11, letterSpacing: '0.04em', color: 'var(--text-secondary)' }}>
          {user.role}
        </span>
      </span>
      <button
        type="button"
        className="icon-btn"
        onClick={() => auth.logout()}
        aria-label="Log out"
        title="Log out"
      >
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <path d="M14 4h4a1.5 1.5 0 0 1 1.5 1.5v13A1.5 1.5 0 0 1 18 20h-4" {...ICON_STROKE} />
          <path d="M9.5 8 5.5 12l4 4" {...ICON_STROKE} />
          <path d="M5.5 12h9" {...ICON_STROKE} />
        </svg>
      </button>
    </div>
  );
}

export function AppLayout() {
  const [dark, toggleTheme] = useTheme();
  const queryClient = useQueryClient();

  /**
   * Set by the api client (SESSION_EXPIRED_EVENT) when a data endpoint answers
   * 401 — the session died mid-use. The cache is dropped (every answer in it
   * came from the dead session) and `/auth/me` re-asked, which flips the layout
   * onto the signed-out card below. No OAuth redirect fires from here: when the
   * callback hop fails, a redirect loop ends on a raw error; a visible
   * "Sign in" click always recovers.
   */
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    const onExpired = (): void => {
      setExpired(true);
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'auth' });
      void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [queryClient]);

  const me = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: ({ signal }) => auth.me(signal),
    staleTime: 60_000,
    retry: false,
  });

  // A 401 was a one-off (blip, proxy hiccup) and /auth/me still sees the
  // session: leave the expired state immediately instead of wedging the card.
  useEffect(() => {
    if (me.data?.authenticated) setExpired(false);
  }, [me.data?.authenticated]);

  /**
   * Signed out with auth enabled (`authenticated:false` from the fresh visit,
   * or a 401 mid-session): the shell is not rendered at all, so no page query
   * ever mounts to 401 — the raw "GET /subscriptions failed with HTTP 401"
   * screen this layout used to show instead of a login is exactly what this
   * early return exists to prevent.
   */
  const signedOut = me.data != null && !me.data.authDisabled && !me.data.authenticated;
  if (signedOut || expired) {
    return (
      <SignedOutCard
        title={expired ? 'Your session has expired' : 'Sign in to continue'}
        hint="This workspace is private. Sign in with your Yandex ID to continue."
      />
    );
  }
  if (me.data == null) {
    // Identity probe still in flight: render nothing rather than the shell, or
    // its queries (NotificationBell, page data) fire in that gap and 401 on
    // every signed-out visit. If the probe hard-failed (API unreachable) fall
    // through — the pages render their own error states, and a blank page
    // helps nobody.
    if (!me.isError) return null;
  }

  return (
    <ToastProvider>
      <div className="shell">
        <aside className="sidebar">
          <div className="sidebar__brand">
            <span className="sidebar__logo" aria-hidden="true">iS</span>
            <span className="sidebar__title">Subscriptions</span>
          </div>
          <nav className="sidebar__nav" aria-label="Main navigation">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  isActive ? 'sidebar__link sidebar__link--active' : 'sidebar__link'
                }
              >
                <span className="sidebar__icon">{item.icon}</span>
                <span className="sidebar__label">{item.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="sidebar__footer">
            <ApiStatusChips />
          </div>
        </aside>

        <div className="shell__main">
          <header className="topbar">
            <GlobalSearch />
            <div className="topbar__spacer" />
            <button type="button" className="icon-btn" onClick={toggleTheme} aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}>
              {dark ? (
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                  <circle cx="12" cy="12" r="4" {...ICON_STROKE} />
                  <path d="M12 3v2m0 14v2M3 12h2m14 0h2M5.6 5.6 7 7m10 10 1.4 1.4M5.6 18.4 7 17M17 7l1.4-1.4" {...ICON_STROKE} />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                  <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" {...ICON_STROKE} />
                </svg>
              )}
            </button>
            <NotificationBell />
            <UserChip />
          </header>
          <main className="page">
            <Outlet />
          </main>
        </div>
      </div>
    </ToastProvider>
  );
}
